import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowUp, ArrowDown, ArrowLeft, ArrowRight, RotateCcw } from "lucide-react";

const SIZE = 4;
const BEST_KEY = "ng_2048_best";

const empty = () => Array.from({ length: SIZE }, () => Array(SIZE).fill(0));

function addRandom(board) {
  const free = [];
  for (let r = 0; r < SIZE; r++)
    for (let c = 0; c < SIZE; c++) if (board[r][c] === 0) free.push([r, c]);
  if (!free.length) return board;
  const [r, c] = free[Math.floor(Math.random() * free.length)];
  board[r][c] = Math.random() < 0.9 ? 2 : 4;
  return board;
}

function clone(b) {
  return b.map((row) => row.slice());
}

// Slide + merge a single row to the left. Returns { row, gained, moved }.
function collapse(row) {
  const nums = row.filter((n) => n !== 0);
  let gained = 0;
  for (let i = 0; i < nums.length - 1; i++) {
    if (nums[i] === nums[i + 1]) {
      nums[i] *= 2;
      gained += nums[i];
      nums.splice(i + 1, 1);
    }
  }
  while (nums.length < SIZE) nums.push(0);
  const moved = nums.some((n, i) => n !== row[i]);
  return { row: nums, gained, moved };
}

const rotateCW = (b) =>
  b[0].map((_, c) => b.map((row) => row[c]).reverse());
const rotateCCW = (b) =>
  b[0].map((_, c) => b.map((row) => row[SIZE - 1 - c]));

// direction: 0 left, 1 up, 2 right, 3 down
function move(board, dir) {
  let work = clone(board);
  for (let i = 0; i < dir; i++) work = rotateCW(work);
  let gained = 0;
  let moved = false;
  work = work.map((row) => {
    const res = collapse(row);
    gained += res.gained;
    if (res.moved) moved = true;
    return res.row;
  });
  for (let i = 0; i < dir; i++) work = rotateCCW(work);
  return { board: work, gained, moved };
}

function canMove(board) {
  for (let r = 0; r < SIZE; r++)
    for (let c = 0; c < SIZE; c++) {
      if (board[r][c] === 0) return true;
      if (c < SIZE - 1 && board[r][c] === board[r][c + 1]) return true;
      if (r < SIZE - 1 && board[r][c] === board[r + 1][c]) return true;
    }
  return false;
}

function freshBoard() {
  return addRandom(addRandom(empty()));
}

// Tile visual style — warm monochrome ramp on the paper theme.
// NOTE: this theme redefines Tailwind `white` -> ink (dark) and `black` -> paper,
// so `bg-white/[x]` = a dark ink wash and `text-cream`/`text-white` = dark text,
// while `text-ink` = light text and `bg-surface` = true white (#FFFFFF).
// Tiles climb from white → ink; text flips to light once the wash gets dark.
function tileStyle(v) {
  const map = {
    0: "bg-white/[0.05] text-transparent",                 // recessed empty well
    2: "bg-surface text-cream ring-1 ring-white/[0.06]",   // white tile, dark text
    4: "bg-surface text-cream ring-1 ring-white/[0.14]",
    8: "bg-white/[0.16] text-cream",
    16: "bg-white/[0.27] text-cream",
    32: "bg-white/[0.4] text-cream",
    64: "bg-white/[0.55] text-ink",                        // dark tile, light text
    128: "bg-white/[0.68] text-ink",
    256: "bg-white/[0.8] text-ink",
    512: "bg-white/[0.9] text-ink",
    1024: "bg-cream text-ink",
    2048: "bg-cream text-ink",
  };
  return map[v] || "bg-cream text-ink";
}

export default function Game2048() {
  const [board, setBoard] = useState(freshBoard);
  const [score, setScore] = useState(0);
  const [best, setBest] = useState(0);
  const [over, setOver] = useState(false);
  const [won, setWon] = useState(false);
  const touch = useRef(null);

  useEffect(() => {
    const stored = Number(localStorage.getItem(BEST_KEY) || 0);
    setBest(stored);
  }, []);

  useEffect(() => {
    if (score > best) {
      setBest(score);
      localStorage.setItem(BEST_KEY, String(score));
    }
  }, [score, best]);

  const doMove = useCallback(
    (dir) => {
      if (over) return;
      setBoard((prev) => {
        const { board: next, gained, moved } = move(prev, dir);
        if (!moved) return prev;
        addRandom(next);
        setScore((s) => s + gained);
        if (!won && next.some((row) => row.includes(2048))) setWon(true);
        if (!canMove(next)) setOver(true);
        return next;
      });
    },
    [over, won]
  );

  const reset = () => {
    setBoard(freshBoard());
    setScore(0);
    setOver(false);
    setWon(false);
  };

  useEffect(() => {
    const onKey = (e) => {
      const map = {
        ArrowLeft: 0, ArrowUp: 1, ArrowRight: 2, ArrowDown: 3,
        a: 0, w: 1, d: 2, s: 3,
      };
      if (e.key in map) {
        e.preventDefault();
        doMove(map[e.key]);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [doMove]);

  const onTouchStart = (e) => {
    const t = e.touches[0];
    touch.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e) => {
    if (!touch.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touch.current.x;
    const dy = t.clientY - touch.current.y;
    if (Math.max(Math.abs(dx), Math.abs(dy)) < 24) return;
    if (Math.abs(dx) > Math.abs(dy)) doMove(dx > 0 ? 2 : 0);
    else doMove(dy > 0 ? 3 : 1);
    touch.current = null;
  };

  return (
    <div className="flex flex-col items-center">
      {/* Scoreboard */}
      <div className="w-full max-w-md flex items-center justify-between gap-4 mb-5">
        <div className="flex gap-3">
          <Stat label="Score" value={score} />
          <Stat label="Best" value={best} />
        </div>
        <button
          onClick={reset}
          data-testid="game2048-reset"
          className="inline-flex items-center gap-2 rounded-full border border-white/20 text-cream text-sm font-medium px-5 py-2.5 hover:bg-white/10 transition-colors"
        >
          <RotateCcw size={15} /> New game
        </button>
      </div>

      {/* Board */}
      <div
        data-testid="game2048-board"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        className="relative rounded-2xl border border-white/10 bg-surface-2 p-3 sm:p-4 select-none touch-none"
        style={{ width: "min(92vw, 28rem)" }}
      >
        <div className="grid grid-cols-4 gap-3 sm:gap-4">
          {board.flat().map((v, i) => (
            <div
              key={i}
              className={`aspect-square rounded-xl flex items-center justify-center font-heading font-medium tabular-nums transition-colors duration-150 ${tileStyle(
                v
              )} ${v >= 1000 ? "text-2xl sm:text-3xl" : "text-3xl sm:text-4xl"}`}
            >
              {v !== 0 ? v : ""}
            </div>
          ))}
        </div>

        {(over || won) && (
          <div className="absolute inset-0 rounded-2xl bg-ink/80 backdrop-blur-sm flex flex-col items-center justify-center gap-4">
            <p className="font-heading font-light text-3xl sm:text-4xl tracking-tight text-cream">
              {won ? "You hit 2048 🎉" : "Game over"}
            </p>
            <p className="text-ash text-sm">Score · {score}</p>
            <div className="flex gap-3">
              {won && !over && (
                <button
                  onClick={() => setWon(false)}
                  className="rounded-full border border-white/20 text-cream font-medium px-6 py-3 hover:bg-white/10 transition-colors"
                >
                  Keep going
                </button>
              )}
              <button
                onClick={reset}
                className="rounded-full bg-cream text-ink font-medium px-6 py-3 hover:bg-white transition-colors"
              >
                Play again
              </button>
            </div>
          </div>
        )}
      </div>

      {/* On-screen pad (mobile / mouse) */}
      <div className="mt-6 grid grid-cols-3 gap-2 w-40 sm:hidden">
        <span />
        <Pad onClick={() => doMove(1)}><ArrowUp size={18} /></Pad>
        <span />
        <Pad onClick={() => doMove(0)}><ArrowLeft size={18} /></Pad>
        <Pad onClick={() => doMove(3)}><ArrowDown size={18} /></Pad>
        <Pad onClick={() => doMove(2)}><ArrowRight size={18} /></Pad>
      </div>

      <p className="mt-6 text-ash text-xs tracking-[0.12em] uppercase">
        Arrow keys / WASD · or swipe
      </p>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="rounded-xl border border-white/10 bg-surface px-4 py-2 text-center min-w-[5rem]">
    <p className="text-[0.65rem] tracking-[0.15em] uppercase text-ash">{label}</p>
    <p className="font-heading text-xl text-cream tabular-nums">{value}</p>
  </div>
);

const Pad = ({ onClick, children }) => (
  <button
    onClick={onClick}
    className="h-12 rounded-xl border border-white/15 text-cream flex items-center justify-center hover:bg-white/10 transition-colors"
  >
    {children}
  </button>
);
